import React, { FC, memo, useEffect } from "react"
import css from "./BookingPricePreview.module.css"
import {
    AdditionalItemMode,
    Currency,
    GenerateScheduledPaymentsByCourseMutation,
    GetCourseDetailsByIdPublicQuery,
    NewBookingInput,
    PaymentInterval,
    PaymentType,
    useGenerateScheduledPaymentsByCourseMutation,
    useGetAppSettingsInvoiceDueDaysQuery,
} from "../../../../generated/graphql"
import { Alert, Button, Checkbox, Input, Radio, Spin, Switch, Tag } from "antd"
import { CheckOutlined } from "@ant-design/icons"
import { margin } from "../../../../styles/layout"
import { NewBookingInputKeys } from "../BookingSummary"
import MonthlyPaymentList from "./MonthlyPaymentList"
import moment from "moment"
import { renderAmount } from "../../../../helpers/renderAmount"
import classnames from "classnames"
import { useCurrency } from "../../../../helpers/currencyHelper"
import MemberDiscount from "./MemberDiscount"
import { useTranslation } from "react-i18next"
import { buildQuoteInfo, QuoteInfo, QUOTE_ERROR } from "../lateEntryQuote"
import { discountCodeRejectText, getDiscountCodeRejection, normalizeDiscountCodeInput, rememberAppliedDiscount } from "./discountCodeMessages"
import { forgetLinkDiscountCode } from "./linkDiscountCode"
import { keepCourseAdditionalItemIds } from "./additionalItemSelection"
import { courseOffersPaymentTypeChoice, resolvePaymentTypeChoice } from "./paymentTypeChoice"

export type FetchedCourse = GetCourseDetailsByIdPublicQuery["courseByIdPublic"]

type BookingPricePreviewProps = {
    course: FetchedCourse
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    bookingData: NewBookingInput
    bookerCredit?: number | null
    creditRedemptionEnabled?: boolean
    /** Tenant-Flag featureDiscountCodes (settingEndUserAppPublic.discountCodesEnabled): zeigt das Code-Feld. */
    discountCodesEnabled?: boolean
    /**
     * Web-Setting showTotalPriceInCheckout: steuert den Gesamtpreis-Block am Ende
     * der Preisübersicht. Default true (auch für alte Backends ohne das Feld).
     */
    showTotalPrice?: boolean
    onOpenAmountChange?: (openAmount: number) => void
    /**
     * Meldet die zuletzt final gerenderte Preview-Quote an BookingSummary — bzw. `null`
     * sofort beim Start jedes neuen Preview-Requests (Invalidierung beim Request-Start).
     * Trägt Fingerprint + Kalkulationsversion für den atomaren Submit-Vertrag.
     */
    onQuoteChange?: (quote: QuoteInfo | typeof QUOTE_ERROR | null) => void
    /**
     * Zähler, den BookingSummary bei validUntil-Ablauf oder PRICE_QUOTE_CHANGED erhöht,
     * um einen frischen Preview-Request zu erzwingen (zusätzliche useEffect-Dep unten).
     */
    refreshNonce?: number
    /**
     * Die Preview ist mit gewählten Zusatzposten gescheitert. BookingSummary lädt den Kurs dann neu,
     * weil der Snapshot aus den Kursdetails einen inzwischen gelöschten Posten noch zeigen kann.
     */
    onCourseOutdated?: () => void
    /** Voraussetzung fehlt: Es entsteht ein Wartelistenplatz, auch wenn der Kurs freie Plätze hat. */
    waitlistOnly?: boolean
}

const BookingPricePreview: FC<BookingPricePreviewProps> = ({
    course,
    updateBookingData,
    bookingData,
    bookerCredit,
    creditRedemptionEnabled,
    discountCodesEnabled,
    showTotalPrice,
    onOpenAmountChange,
    onQuoteChange,
    refreshNonce,
    onCourseOutdated,
    waitlistOnly,
}) => {
    const { t, i18n } = useTranslation()
    // MwSt.-Sätze sind nicht zwingend ganzzahlig (Schweiz: 8,1 % / 2,6 %): bis zu zwei
    // Nachkommastellen, ganzzahlige Sätze bleiben ohne ("19 %"). Locale folgt der aktiven
    // UI-Sprache, damit im englischen Checkout "8.1 %" statt "8,1 %" steht; die vier
    // Sprachen (de, de-DU, de-SIE, en) fallen dafür auf ihre Basissprache zurück.
    // Der Helper renderPercent passt hier nicht: er erzwingt minimumFractionDigits
    // und würde aus "19 %" ein "19,00 %" machen.
    // Fallback "de", falls keine i18n-Instanz initialisiert ist: dann bleibt es beim
    // bisherigen Verhalten statt unvermittelt auf englische Formatierung zu springen.
    const vatLocale = (i18n.language ?? "de").startsWith("de") ? "de" : "en"
    const vatRateFormatter = React.useMemo(() => new Intl.NumberFormat(vatLocale, { maximumFractionDigits: 2 }), [vatLocale])
    const [discountInEuro, setDiscountInEuro] = React.useState<number | null>(null)
    const [discountInPercentage, setDiscountInPercentage] = React.useState<number | null>(null)
    const [applyCredit, setApplyCredit] = React.useState(false)
    const [baseGrossPrice, setBaseGrossPrice] = React.useState<number | null>(null)
    const booking = bookingData
    const attendeeId = booking.attendeeId ?? undefined
    const currency = useCurrency()
    const availableCredit = Math.max(0, Number(bookerCredit ?? 0))
    // Echter Platz statt Warteliste (siehe BookingSummary `mode`).
    const bookingGetsPlace = course.freePlaces > 0 && !waitlistOnly
    // Ist der Kurs voll, entsteht ein Wartelistenplatz (siehe BookingSummary `mode`). Der startet
    // keinen Zahlungsfluss, also darf hier auch kein Guthaben angerechnet werden.
    const shouldShowCreditRedemption = creditRedemptionEnabled === true && availableCredit > 0 && bookingGetsPlace
    // Gewählte Zahlweise, wenn der Kurs beide anbietet, sonst die des Kurses (gleiche Regel wie Submit und Server).
    const effectivePaymentType = resolvePaymentTypeChoice(booking.paymentType, course)

    const [preview, setPreview] = React.useState<GenerateScheduledPaymentsByCourseMutation["generateScheduledPaymentsByCourse"]>()
    // §4.5: expliziter Preview-Status — "loading" markiert den zuletzt
    // bestätigten Preis als veraltet (keine Enforcement-Ableitung aus einer
    // alten Generation), "error" ersetzt den Preisblock durch Fehler + Retry
    // statt still auf den Kurs-Listenpreis zurückzufallen.
    const [previewStatus, setPreviewStatus] = React.useState<"loading" | "fresh" | "error">("loading")
    const [retryNonce, setRetryNonce] = React.useState(0)
    // Rabattcode: bookingData ist die Wahrheit (persistiert, geht mit in createBooking). Geprüft wird er vom Server
    // in der Preview; eine Ablehnung entfernt ihn wieder, die Buchung bleibt zum normalen Preis möglich.
    // Normalisiert wie der Server (Großschreibung, ohne Leerzeichen), damit ein Link-Code „alle10“ zum Preview-Wert passt.
    // Ohne Mandanten-Flag zählt auch kein gespeicherter oder verlinkter Code.
    const appliedDiscountCode = discountCodesEnabled ? normalizeDiscountCodeInput(booking.discountCode ?? "") : ""
    const [discountCodeInput, setDiscountCodeInput] = React.useState(appliedDiscountCode)
    const [discountCodeError, setDiscountCodeError] = React.useState<string | null>(null)
    const [generatePreview] = useGenerateScheduledPaymentsByCourseMutation()
    // useCurrency liefert anfangs EUR und zieht per Effect nach; der Preview-Effect liest deshalb den aktuellen Stand über eine Ref.
    const currencyShortRef = React.useRef(currency.short)
    currencyShortRef.current = currency.short
    // Zusatzposten: explizit gewählte optionale Posten. Pflichtposten erzwingt der
    // Server selbst — sie erscheinen als AdditionalItem-Raten in der Preview.
    const additionalItemsOfCourse = course.additionalItems ?? []
    // ZP-12: bookingData (localStorage-persistiert) ist die Source of Truth der
    // Auswahl — der State initialisiert daraus, damit Reload/Remount dieselbe
    // Auswahl rechnen, anzeigen und submitten. IDs, die dieser Kurs nicht (mehr)
    // anbietet, fallen weg — sonst lehnt der Server jede Preview ab (Sackgasse).
    const [selectedAdditionalItemIds, setSelectedAdditionalItemIds] = React.useState<Array<string>>(() => keepCourseAdditionalItemIds(booking.additionalItemIds, course))
    // §3.4: Bei vollem Kurs entsteht ein WARTELISTEN-Platz, der in V1 keine
    // Zusatzposten trägt. Die Auswahl auszublenden genügt nicht — bereits
    // persistierte IDs (aus bookingData/localStorage, gewählt bevor der Kurs voll
    // war) dürfen weder in die Preview noch in den Submit gelangen.
    const additionalItemsAllowed = bookingGetsPlace
    // Gegen den aktuellen Kurs filtern, nicht nur beim Mount: lädt BookingSummary den Kurs neu (Posten
    // gelöscht), fällt die ID hier weg, der Key unten ändert sich und die Preview rechnet ohne sie.
    const effectiveAdditionalItemIds = additionalItemsAllowed ? keepCourseAdditionalItemIds(selectedAdditionalItemIds, course) : []
    // Für die Effect-Dependency als stabiler String
    const effectiveAdditionalItemIdsKey = effectiveAdditionalItemIds.join(",")
    // Generation-Counter gegen Out-of-Order-Responses: bei jedem Effect-Lauf inkrementiert,
    // jede setState-Stelle prueft davor, ob ihre Antwort noch die aktuellste ist.
    const requestIdRef = React.useRef(0)

    useEffect(() => {
        if (!shouldShowCreditRedemption && applyCredit) {
            setApplyCredit(false)
        }
    }, [applyCredit, shouldShowCreditRedemption])

    // Neu geladener Kurs: nicht mehr angebotene Posten auch aus der lokalen Auswahl nehmen. Sonst schreibt der
    // nächste Klick auf einen anderen Posten sie zurück, und käme der Posten wieder, wäre er still gewählt.
    useEffect(() => {
        setSelectedAdditionalItemIds((previous) => {
            const kept = keepCourseAdditionalItemIds(previous, course)
            return kept.length === previous.length ? previous : kept
        })
    }, [course])

    useEffect(() => {
        // Wartelisten-Fall: persistierte Auswahl aus dem bookingData entfernen, sonst
        // landet sie im Submit. Leeres Array statt Feld-Löschung, weil
        // updateBookingData null/undefined ignoriert; die Alt-Backend-Kompatibilität
        // bleibt gewahrt, da wir nur schreiben, wenn das Feld ohnehin schon gesetzt war.
        if (!additionalItemsAllowed && (booking.additionalItemIds ?? []).length > 0) {
            updateBookingData("additionalItemIds", [])
        }
        // Lokale Auswahl mit zurücksetzen: Wechselt der Teilnehmer zurück auf einen echten Platz,
        // zeigten Checkbox und Preview sonst wieder den Posten, der Submit schickte aber [] (PRICE_QUOTE_CHANGED).
        if (!additionalItemsAllowed) {
            setSelectedAdditionalItemIds((previous) => (previous.length > 0 ? [] : previous))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [additionalItemsAllowed, booking.additionalItemIds])

    useEffect(() => {
        requestIdRef.current += 1
        const requestId = requestIdRef.current
        // Invalidierung BEIM Request-Start (nicht erst beim Response): jeder neue Preview-
        // Request macht die alte Quote sofort ungültig, damit BookingSummary den Submit
        // sperrt, solange kein frischer Fingerprint vorliegt.
        onQuoteChange?.(null)
        // §4.5: sichtbaren Zustand ebenfalls sofort als veraltet markieren.
        setPreviewStatus("loading")

        const getPreview = async () => {
            try {
                const baseRes = await generatePreview({
                    variables: {
                        courseId: course.id,
                        attendeeId: attendeeId,
                        omitMemberDiscount: false,
                        paymentType: effectivePaymentType,
                        memberDiscount: discountInEuro,
                        ...(appliedDiscountCode ? { discountCode: appliedDiscountCode } : {}),
                        // Nur bei tatsächlicher Auswahl mitsenden — leere Auswahl und
                        // weggelassenes Feld sind serverseitig äquivalent (Pflichtposten
                        // erzwingt der Server ohnehin selbst).
                        ...(effectiveAdditionalItemIds.length > 0 ? { additionalItemIds: effectiveAdditionalItemIds } : {}),
                    },
                })
                if (requestId !== requestIdRef.current) return
                const basePreview = baseRes.data?.generateScheduledPaymentsByCourse

                if (!basePreview) {
                    return
                }

                setBaseGrossPrice(basePreview.grossPrice)

                // ZP-08: Guthaben ist NIE auf Zusatzposten anwendbar — der Cap
                // läuft auf der Kursbasis (grossPrice minus AdditionalItem-Raten),
                // exakt wie im Server-Create. Sonst weichen persistiertes
                // Guthaben und Fingerprint ab (PRICE_QUOTE_CHANGED beim Submit).
                const additionalItemsGross = basePreview.scheduledPayments
                    .filter((sp) => Boolean(sp.additionalItemId))
                    .reduce((acc, sp) => acc + Number(sp.paymentAmount), 0)
                const creditableBase = Math.max(0, Number((basePreview.grossPrice - additionalItemsGross).toFixed(2)))

                const creditToApply =
                    applyCredit && shouldShowCreditRedemption
                        ? Math.min(availableCredit, creditableBase)
                        : 0

                let nextPreview = basePreview

                if (creditToApply > 0) {
                    const creditedRes = await generatePreview({
                        variables: {
                            courseId: course.id,
                            attendeeId: attendeeId,
                            omitMemberDiscount: false,
                            paymentType: effectivePaymentType,
                            memberDiscount: discountInEuro,
                            ...(appliedDiscountCode ? { discountCode: appliedDiscountCode } : {}),
                            creditedBalance: creditToApply,
                            ...(effectiveAdditionalItemIds.length > 0 ? { additionalItemIds: effectiveAdditionalItemIds } : {}),
                        },
                    })
                    if (requestId !== requestIdRef.current) return
                    nextPreview = creditedRes.data?.generateScheduledPaymentsByCourse ?? basePreview
                }

                if ((booking.creditedBalance ?? 0) !== creditToApply) {
                    updateBookingData("creditedBalance", creditToApply)
                }
                onOpenAmountChange?.(nextPreview.grossPrice)
                // Nur der Response, dessen requestId noch aktuell ist (Guard oben), meldet
                // seine Quote — insbesondere beim Guthaben-Zweitcall gilt allein der finale.
                onQuoteChange?.(buildQuoteInfo(nextPreview))
                setPreview(nextPreview)
                setPreviewStatus("fresh")
                // Für die Ergebnisseite merken („Rabattcode X eingelöst, du sparst Y“).
                rememberAppliedDiscount(nextPreview.discountCode && (nextPreview.discount ?? 0) > 0 ? { code: nextPreview.discountCode, amount: nextPreview.discount ?? 0, currency: currencyShortRef.current } : null)
            } catch (error) {
                if (requestId !== requestIdRef.current) return
                const rejection = getDiscountCodeRejection(error)
                if (rejection && appliedDiscountCode) {
                    // Code abgelehnt: Grund anzeigen und Code entfernen – der Effect rechnet ohne Code neu,
                    // ein falscher Code blockiert keine Buchung.
                    setDiscountCodeError(discountCodeRejectText(rejection.reason, t))
                    rememberAppliedDiscount(null)
                    updateBookingData("discountCode", "")
                    return
                }
                console.error(error)
                // Mit gewählten Zusatzposten kann der Kurs-Snapshot veraltet sein (Posten gelöscht, Feature aus).
                // Ohne Code-Prüfung: für Konten mit weiteren Rollen verpackt der Server den Fehler als BadRequest.
                if (effectiveAdditionalItemIds.length > 0) onCourseOutdated?.()
                // Fehlerzustand explizit melden, sonst bliebe die Quote für immer `null`
                // (Gate zeigte dauerhaft "wird berechnet", kein Retry-Pfad, F1).
                onQuoteChange?.(QUOTE_ERROR)
                // ZP-12: Alte Preview invalidieren — enforcedByServer darf nie aus
                // einer überholten Preview abgeleitet werden (abgewähltes Item wirkte
                // sonst fälschlich als servererzwungen und die Checkbox fror ein).
                setPreview(undefined)
                // §4.5: Fehler ersetzt den Preisblock (kein irreführender
                // Fallback auf den Kurs-Listenpreis) und bietet Retry an.
                setPreviewStatus("error")
                // Anfrage ist fehlgeschlagen, waehrend Guthaben nicht (mehr) angerechnet werden soll
                // (Toggle-off oder Flag/Verfuegbarkeit weg) — kein verstecktes Alt-Guthaben im Booking lassen.
                if (!applyCredit && (booking.creditedBalance ?? 0) !== 0) {
                    updateBookingData("creditedBalance", 0)
                }
            }
        }

        getPreview()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendeeId, course.id, effectivePaymentType, discountInEuro, applyCredit, shouldShowCreditRedemption, availableCredit, refreshNonce, retryNonce, effectiveAdditionalItemIdsKey, appliedDiscountCode])

    // Zahlweisen-Schalter: nur bei Kursen mit beiden Zahlweisen. Als JSX-Ausdruck statt innerer Komponente, sonst
    // mountet jeder Render die Radio-Gruppe neu; gesteuert über effectivePaymentType, damit Anzeige und Rechnung
    // dieselbe Zahlweise zeigen.
    const paymentTypeSwitch = courseOffersPaymentTypeChoice(course) ? (
        // antd 4 setzt an Radio.Group keine role; ohne radiogroup läsen Screenreader das Label nicht vor.
        <div role='radiogroup' aria-label={t("pricePreview.paymentTypeLabel", "Zahlweise") as string} style={{ marginTop: margin.s }}>
            <Radio.Group
                style={{ width: "100%" }}
                value={effectivePaymentType}
                buttonStyle='solid'
                name='paymentType'
                onChange={(e) => updateBookingData("paymentType", e.target.value)}
            >
                <Radio.Button style={{ width: "50%", textAlign: "center" }} value={PaymentType.PrePaid}>
                    {t("pricePreview.paymentTypePrePaid", "Vorkasse")}
                </Radio.Button>
                <Radio.Button style={{ width: "50%", textAlign: "center" }} value={PaymentType.Monthly}>
                    {t("pricePreview.paymentTypeMonthly", "Monatlich")}
                </Radio.Button>
            </Radio.Group>
        </div>
    ) : null

    const TotalPrice = ({
        paymentType,
        preview,
        baseGrossPrice: baseGrossPriceForBreakdown,
        creditToApply: creditToApplyForBreakdown,
        remainingCredit: remainingCreditForBreakdown,
    }: {
        paymentType?: PaymentType
        preview?: GenerateScheduledPaymentsByCourseMutation["generateScheduledPaymentsByCourse"]
        baseGrossPrice?: number | null
        creditToApply?: number
        remainingCredit?: number
    }) => {
        const coursePrice = course?.coursePrice?.[0]

        let totalGrossPrice = preview?.grossPrice ?? coursePrice?.grossPrice ?? 0
        let totalNetPrice = preview?.netPrice ?? coursePrice?.netPrice ?? 0
        const totalVat = coursePrice?.vat ?? 0

        const lessonCount = course?.lessonCount ?? 1
        if (course?.paymentInterval === PaymentInterval.PerLesson && !preview) {
            // Fallback vor Preview-Antwort: coursePrice[0] ist der Preis EINER Lektion, Client multipliziert.
            // Sobald preview da ist, liefert der Server bereits die multiplizierte Gesamtsumme (scheduledPayment.service.ts) — nicht nochmal multiplizieren.
            const coursePricePerLesson = coursePrice.grossPrice
            const netPricePerLesson = coursePrice.netPrice ?? coursePrice.grossPrice

            totalGrossPrice = coursePricePerLesson * lessonCount
            totalNetPrice = netPricePerLesson * lessonCount
            // totalVat bleibt unverändert - VAT-Satz ist ein Prozentsatz, keine absolute Zahl
        }

        // R3-B05: Mischsatz-Ausweis — tragen Preview-Raten einen eigenen
        // eingefrorenen Satz (Zusatzposten), werden alle wirksamen Sätze
        // ausgewiesen ("19 % / 7 %") statt fälschlich eines Einzelsatzes.
        const effectiveVatRates = new Set<number>()

        ;(preview?.scheduledPayments ?? []).forEach((sp) => {
            const rowVat = (sp as { vat?: number | null }).vat

            effectiveVatRates.add(rowVat !== undefined && rowVat !== null ? Number(rowVat) : totalVat)
        })
        if (effectiveVatRates.size === 0) {
            effectiveVatRates.add(totalVat)
        }
        const vatLabel = Array.from(effectiveVatRates)
            .sort((a, b) => b - a)
            .map((rate) => `${vatRateFormatter.format(rate * 100)} %`)
            .join(" / ")

        // §4.4: "Kursgebühr" ohne Zusatzposten ausweisen — Items stehen als
        // eigene Zeile, sonst wirkt das Guthaben auf einen falschen Basiswert.
        const breakdownAdditionalItemsGross = (preview?.scheduledPayments ?? [])
            .filter((sp) => Boolean(sp.additionalItemId))
            .reduce((acc, sp) => acc + Number(sp.paymentAmount), 0)
        const breakdownCourseBase = Number((((baseGrossPriceForBreakdown ?? totalGrossPrice) as number) - breakdownAdditionalItemsGross).toFixed(2))

        const { data, loading } = useGetAppSettingsInvoiceDueDaysQuery()

        const invoiceDueDays = data?.settings?.invoiceDueDays

        const firstCourseLessonDate = course?.lessons[0].startDateTime

        if (!firstCourseLessonDate) {
            return null
        }

        const paymentDue = moment(firstCourseLessonDate).subtract(invoiceDueDays, "days").toISOString()

        if (loading) return <Spin />

        // Rabattcode: der Server liefert den tatsächlich angewandten Betrag; der Basispreis der
        // Preview ist bereits um ihn gekürzt, die Aufschlüsselung zeigt deshalb Kurspreis vor Code.
        const codeDiscountApplied = preview?.discountCode && (preview.discount ?? 0) > 0 ? Number(preview.discount) : 0
        const showBreakdown = Boolean(creditToApplyForBreakdown && creditToApplyForBreakdown > 0) || codeDiscountApplied > 0

        // Zusatzposten-Einzelzeilen entfallen hier bewusst: die Checkbox-Sektion mit
        // Einzelpreisen steht direkt über diesem Block; nur bei aktivem Guthaben oder
        // Rabattcode zeigt die Aufschlüsselung ihre Summe als eigene Zeile (§4.4).
        return (
            <div className={css.priceContainer}>
                {showBreakdown && (
                    <>
                        <div className={classnames(css.totalPriceSub, css.spaceBetween)}>
                            <div>{t("creditRedemption.coursePrice", "Kursgebühr")}</div>
                            <div>{renderAmount({ amount: Number((breakdownCourseBase + codeDiscountApplied).toFixed(2)), currency: currency.short as Currency })}</div>
                        </div>
                        {breakdownAdditionalItemsGross > 0 && (
                            <div className={classnames(css.totalPriceSub, css.spaceBetween)}>
                                <div>{t("additionalItems.breakdownLabel", "Zusatzposten")}</div>
                                <div>{renderAmount({ amount: breakdownAdditionalItemsGross, currency: currency.short as Currency })}</div>
                            </div>
                        )}
                        {codeDiscountApplied > 0 && (
                            <div className={classnames(css.totalPriceSub, css.spaceBetween, css.creditAppliedRow)} data-testid='discount-code-row'>
                                <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>{t("discountCode.row", { defaultValue: "Rabattcode {{code}}", code: preview?.discountCode })}</div>
                                <div style={{ whiteSpace: "nowrap", marginLeft: 8 }}>{`- ${renderAmount({ amount: codeDiscountApplied, currency: currency.short as Currency })}`}</div>
                            </div>
                        )}
                        {Boolean(creditToApplyForBreakdown && creditToApplyForBreakdown > 0) && (
                            <div className={classnames(css.totalPriceSub, css.spaceBetween, css.creditAppliedRow)}>
                                <div>{t("creditRedemption.applied", "Angerechnetes Guthaben")}</div>
                                <div>{`- ${renderAmount({ amount: creditToApplyForBreakdown as number, currency: currency.short as Currency })}`}</div>
                            </div>
                        )}
                    </>
                )}
                <div className={css.totalPrice}>
                    <div>{"Gesamtpreis"}</div>
                    <div>{renderAmount({ amount: totalGrossPrice, currency: currency.short as Currency })}</div>
                </div>
                {Boolean(creditToApplyForBreakdown && creditToApplyForBreakdown > 0 && (remainingCreditForBreakdown ?? 0) > 0) && (
                    <div className={classnames(css.totalPriceSub, css.spaceBetween)}>
                        <div>{t("creditRedemption.remainingLabel", "Restguthaben")}</div>
                        <div>{renderAmount({ amount: remainingCreditForBreakdown as number, currency: currency.short as Currency })}</div>
                    </div>
                )}
                {paymentType === PaymentType.Monthly ? (
                    <div className={css.totalPriceSub}>
                        {`netto ${renderAmount({
                            amount: totalNetPrice ?? totalGrossPrice,
                            currency: currency.short as Currency,
                        })} |
                        ${vatLabel} Mwst.`}
                    </div>
                ) : (
                    <div className={classnames(css.totalPriceSub, css.spaceBetween)}>
                        <div>{`Zahlbar bis ${moment(paymentDue).format("DD.MM.YY")}`}</div>
                        <div>
                            {`netto ${renderAmount({
                                amount: totalNetPrice ?? totalGrossPrice,
                                currency: currency.short as Currency,
                            })} | ${vatLabel} Mwst.`}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const creditBaseGrossPriceRaw = baseGrossPrice ?? preview?.grossPrice ?? course?.coursePrice?.[0]?.grossPrice ?? 0
    // ZP-08: Anzeige nutzt dieselbe Kursbasis ohne Zusatzposten wie Effect + Server.
    const previewAdditionalItemsGross = (preview?.scheduledPayments ?? [])
        .filter((sp) => Boolean(sp.additionalItemId))
        .reduce((acc, sp) => acc + Number(sp.paymentAmount), 0)
    const creditBaseGrossPrice = Math.max(0, Number((creditBaseGrossPriceRaw - previewAdditionalItemsGross).toFixed(2)))
    const creditToApply = applyCredit && shouldShowCreditRedemption ? Math.min(availableCredit, creditBaseGrossPrice) : 0
    const remainingCredit = Math.max(0, availableCredit - creditToApply)

    // Toggle-off setzt creditedBalance synchron auf 0 zurueck (statt nur ueber den
    // Preview-Effect), damit kein verstecktes Guthaben im Booking haengen bleibt, falls
    // der anschliessende Preview-Request fehlschlaegt oder verworfen wird.
    const handleApplyCreditChange = (checked: boolean) => {
        setApplyCredit(checked)
        if (!checked && (booking.creditedBalance ?? 0) !== 0) {
            updateBookingData("creditedBalance", 0)
        }
    }

    // Als JSX-Ausdruck statt eigener Komponentenfunktion: eine innere FC-Deklaration
    // bekaeme bei jedem Render von BookingPricePreview eine neue Funktionsidentitaet und
    // wuerde den interaktiven Switch bei jedem Tastendruck anderswo im Formular remounten.
    const creditRedemption = !shouldShowCreditRedemption ? null : (
            <div className={css.creditRedemption} data-testid='credit-redemption'>
                <div className={css.creditHeader}>
                    <span>{t("creditRedemption.toggle", "Guthaben anrechnen")}</span>
                    {/* §4.6: programmatisch zugeordneter zugänglicher Name für den Switch */}
                    <Switch checked={applyCredit} onChange={handleApplyCreditChange} aria-label={t("creditRedemption.toggle", "Guthaben anrechnen") as string} />
                </div>
                <div className={css.creditRows}>
                    <div>
                        <span>{t("creditRedemption.available", "Verfügbares Guthaben")}</span>
                        <strong>{renderAmount({ amount: availableCredit, currency: currency.short as Currency })}</strong>
                    </div>
                    {applyCredit && (
                        <>
                            <div>
                                <span>{t("creditRedemption.applied", "Angerechnetes Guthaben")}</span>
                                <strong>
                                    {`- ${renderAmount({
                                        amount: creditToApply,
                                        currency: currency.short as Currency,
                                    })}`}
                                </strong>
                            </div>
                            {remainingCredit > 0 && (
                                <>
                                    {/* §4.4: Restguthaben als konkreter Betrag, nicht nur als Hinweis */}
                                    <div>
                                        <span>{t("creditRedemption.remainingLabel", "Restguthaben")}</span>
                                        <strong>{renderAmount({ amount: remainingCredit, currency: currency.short as Currency })}</strong>
                                    </div>
                                    <div className={css.creditHint}>
                                        {t(
                                            "creditRedemption.remaining",
                                            "Das restliche Guthaben bleibt für spätere Buchungen erhalten.",
                                        )}
                                    </div>
                                </>
                            )}
                            {effectivePaymentType === PaymentType.Monthly && (
                                <div className={css.creditHint}>
                                    {t(
                                        "creditRedemption.monthlyHint",
                                        "Das Guthaben reduziert die Monatsraten anteilig.",
                                    )}
                                </div>
                            )}
                            {preview?.grossPrice === 0 && (
                                <Alert
                                    type='success'
                                    showIcon
                                    message={t(
                                        "creditRedemption.fullyCovered",
                                        "Keine Zahlung erforderlich - der Betrag ist vollständig durch das Guthaben gedeckt.",
                                    )}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        )

    // Rabattcode-Feld (Skizze 04/09): zwischen Zusatzposten und Gesamtpreis. „Einlösen“ schreibt den Code in
    // bookingData, die Preview prüft ihn; gültig = grüner Tag mit Ersparnis, abgelehnt = Grund unter dem Feld.
    const applyDiscountCode = () => {
        const normalized = normalizeDiscountCodeInput(discountCodeInput)

        setDiscountCodeInput(normalized)
        setDiscountCodeError(null)
        if (normalized && normalized !== appliedDiscountCode) {
            // Eigene Eingabe beendet den Flyer-Link: Dieser Code soll auch beim erneuten Öffnen des Kurses bleiben.
            forgetLinkDiscountCode()
            updateBookingData("discountCode", normalized)
        }
    }
    const removeDiscountCode = () => {
        setDiscountCodeInput("")
        setDiscountCodeError(null)
        rememberAppliedDiscount(null)
        // Ausdrücklich entfernt gilt auch für den Flyer-Link. Eine Ablehnung behält ihn: Der Code kann für den nächsten Kurs passen.
        forgetLinkDiscountCode()
        updateBookingData("discountCode", "")
    }
    const discountCodeApplied = previewStatus === "fresh" && Boolean(appliedDiscountCode) && preview?.discountCode === appliedDiscountCode
    const discountCodeAmount = discountCodeApplied ? Number(preview?.discount ?? 0) : 0
    // Bei Monatsraten sieht man die Wirkung erst in den Raten; der Satz nennt deshalb, auf wie viele sie sich verteilt (ohne Zusatzposten).
    const discountCodeRateCount = (preview?.scheduledPayments ?? []).filter((sp) => !sp.additionalItemId).length
    const discountCodeSection = !discountCodesEnabled ? null : (
        <div className={css.creditRedemption} data-testid='discount-code'>
            <div className={css.creditHeader}>
                <span>{t("discountCode.title", "Rabattcode")}</span>
            </div>
            {discountCodeApplied ? (
                <div className={css.creditRows}>
                    {/* Umbruch statt Überlauf: ein langer Code darf „entfernen“ auf dem Handy nicht aus der Box schieben. */}
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}>
                        {/* Grüner Tag wie in Skizze 04, Text in dunklerem Grün für lesbaren Kontrast (antd green-8). */}
                        <Tag
                            color='success'
                            icon={<CheckOutlined />}
                            data-testid='discount-code-applied'
                            style={{ margin: 0, fontSize: 13, lineHeight: "22px", fontWeight: 600, color: "#237804", whiteSpace: "normal", overflowWrap: "anywhere", maxWidth: "100%" }}
                        >
                            {appliedDiscountCode}
                        </Tag>
                        <Button
                            type='link'
                            size='small'
                            onClick={removeDiscountCode}
                            data-testid='discount-code-remove'
                            aria-label={t("discountCode.removeAria", { defaultValue: "Rabattcode {{code}} entfernen", code: appliedDiscountCode }) as string}
                        >
                            {t("discountCode.remove", "entfernen")}
                        </Button>
                    </div>
                    {/* Die Ersparnis ist die Kernaussage der Box: normale Textfarbe statt Hinweisgrau (Kontrast). */}
                    <div style={{ fontSize: 13, color: "#545454" }} data-testid='discount-code-saving'>
                        {bookingGetsPlace
                            ? effectivePaymentType === PaymentType.Monthly && discountCodeRateCount > 1
                                ? t("discountCode.appliedMonthly", {
                                      defaultValue: "Du sparst {{amount}}, verteilt auf alle {{count}} Raten.",
                                      amount: renderAmount({ amount: discountCodeAmount, currency: currency.short as Currency }),
                                      count: discountCodeRateCount,
                                  })
                                : t("discountCode.applied", { defaultValue: "Du sparst {{amount}}.", amount: renderAmount({ amount: discountCodeAmount, currency: currency.short as Currency }) })
                            : t("discountCode.appliedWaitlist", {
                                  defaultValue: "Wird für dich vorgemerkt: Bekommst du einen Platz, sparst du {{amount}}, auch wenn der Code bis dahin abgelaufen ist.",
                                  amount: renderAmount({ amount: discountCodeAmount, currency: currency.short as Currency }),
                              })}
                    </div>
                </div>
            ) : (
                <div className={css.creditRows}>
                    <Input.Group compact style={{ display: "flex" }}>
                        <Input
                            value={discountCodeInput}
                            onChange={(event) => {
                                setDiscountCodeInput(event.target.value)
                                // Der alte Ablehnungsgrund passt nicht mehr zur neuen Eingabe.
                                setDiscountCodeError(null)
                            }}
                            onPressEnter={(event) => {
                                event.preventDefault()
                                applyDiscountCode()
                            }}
                            placeholder={t("discountCode.placeholder", "Code eingeben") as string}
                            aria-label={t("discountCode.title", "Rabattcode") as string}
                            autoCapitalize='characters'
                            autoCorrect='off'
                            spellCheck={false}
                            maxLength={32}
                            data-testid='discount-code-input'
                            status={discountCodeError ? "error" : undefined}
                            aria-invalid={Boolean(discountCodeError)}
                            aria-describedby={discountCodeError ? "discount-code-error" : undefined}
                        />
                        <Button onClick={applyDiscountCode} disabled={!discountCodeInput.trim() || previewStatus === "loading"} data-testid='discount-code-apply'>
                            {t("discountCode.apply", "Einlösen")}
                        </Button>
                    </Input.Group>
                    {discountCodeError && (
                        <div id='discount-code-error' style={{ color: "#cf1322", marginTop: 4 }} role='alert' data-testid='discount-code-error'>
                            {discountCodeError}
                        </div>
                    )}
                    {!discountCodeError && appliedDiscountCode && previewStatus === "loading" && <div className={css.creditHint}>{t("discountCode.checking", "Code wird geprüft …")}</div>}
                </div>
            )}
        </div>
    )

    // Zusatzposten-Auswahl: optionale Posten wählt der Bucher; vom Server erzwungene
    // Posten (Pflicht bzw. Neukunden-Gebühr bei scharfer Erkennung) erscheinen als
    // vorausgewählte, nicht abwählbare Zeilen — Wahrheit ist die Preview-Antwort.
    const handleAdditionalItemToggle = (itemId: string, checked: boolean) => {
        const next = checked ? [...selectedAdditionalItemIds.filter((id) => id !== itemId), itemId] : selectedAdditionalItemIds.filter((id) => id !== itemId)

        setSelectedAdditionalItemIds(next)
        updateBookingData("additionalItemIds", next)
    }

    const additionalItemsSection =
        // §3.4: Bei vollem Kurs entsteht ein WARTELISTEN-Platz — der trägt in V1
        // keine wählbaren Zusatzposten (die Gebühr entsteht erst bei der
        // Promotion zur echten Buchung).
        additionalItemsOfCourse.length === 0 || !additionalItemsAllowed ? null : (
            // ZP-21: fieldset/legend gruppiert die Checkboxen für Screenreader;
            // Preis und Pflicht-Grund hängen per aria-describedby an der Checkbox.
            // §4.6-Nebenbefund: marginTop NACH margin setzen, sonst überschreibt
            // das Shorthand `margin: 0` den Abstand wieder.
            <fieldset style={{ border: 0, margin: 0, padding: 0, marginTop: margin.s }} data-testid='additional-items'>
                <legend style={{ fontWeight: 550, marginBottom: 4, padding: 0 }}>{t("additionalItems.title", "Zusatzposten") as string}</legend>
                {additionalItemsOfCourse.map((item) => {
                    // §4.5: Enforcement-Ableitung NIE aus einer veralteten
                    // Preview-Generation — während "loading"/"error" gilt nur die
                    // lokale Auswahl plus der statische Mandatory-Modus.
                    const enforcedByServer =
                        previewStatus === "fresh" &&
                        Boolean(preview?.scheduledPayments.some((sp) => sp.additionalItemId === item.id)) &&
                        !selectedAdditionalItemIds.includes(item.id)
                    const enforced = item.mode === AdditionalItemMode.Mandatory || enforcedByServer
                    const checked = enforced || selectedAdditionalItemIds.includes(item.id)
                    const describedById = `additional-item-desc-${item.id}`

                    const description = item.description?.trim()

                    return (
                        // Dezente Box pro Posten: grenzt die Posten voneinander ab und
                        // macht sichtbar, dass der Erklärtext zum jeweiligen Posten gehört.
                        <div
                            key={item.id}
                            style={{
                                background: "#fafafa",
                                border: "1px solid #f0f0f0",
                                borderRadius: 6,
                                padding: "6px 10px",
                                marginBottom: 6,
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                {/* §4.6: Pflicht-Checkboxen bleiben FOKUSSIERBAR (kein
                                    disabled) — der Pflichtgrund ist damit auch für
                                    Tastaturnutzer erreichbar; der Toggle wird im
                                    Handler blockiert und per aria-disabled kommuniziert. */}
                                <Checkbox
                                    checked={checked}
                                    aria-disabled={enforced}
                                    className={enforced ? "additional-item-enforced" : undefined}
                                    onChange={(e) => {
                                        if (enforced) return
                                        handleAdditionalItemToggle(item.id, e.target.checked)
                                    }}
                                    aria-describedby={describedById}
                                    data-testid={`additional-item-${item.id}`}
                                >
                                    {item.title}
                                </Checkbox>
                                <span id={describedById} style={{ fontWeight: 600 }}>
                                    {"+ "}
                                    {renderAmount({ amount: Number(item.grossPrice), currency: currency.short as Currency })}
                                    {enforced && (
                                        <span className='visually-hidden-additional-item'>
                                            {" "}
                                            {t("additionalItems.enforcedHint", "Pflichtposten, wird automatisch berechnet") as string}
                                        </span>
                                    )}
                                </span>
                            </div>
                            {/* Hinweistext aus der Posten-Konfiguration — nur anzeigen, wenn befüllt.
                                24px rückt ihn unter das Checkbox-Label (16px Box + 8px Abstand). */}
                            {description && (
                                <div style={{ fontSize: 13, opacity: 0.75, margin: "0 0 0 24px" }} data-testid={`additional-item-description-${item.id}`}>
                                    {description}
                                </div>
                            )}
                        </div>
                    )
                })}
                {/* ponytail: lokale Klassen statt globaler CSS-Änderung */}
                <style>{".visually-hidden-additional-item{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;}.additional-item-enforced{opacity:0.65;cursor:not-allowed;}"}</style>
            </fieldset>
        )

    // Späteinstieg-Ausweis: nur bei tatsächlicher Preisreduktion eine Zeile "Preis für X von Y
    // verbleibenden Kurseinheiten" + Gegenüberstellung regulärer/aktueller Preis (kein
    // "Rabatt"-Wording). Bei Policy "Originalpreis beibehalten" ist isPartialBooking zwar true
    // (nur zukünftige Einheiten werden gebucht), aber reduction === 0 — dann wäre die
    // Gegenüberstellung irreführend. Rein Server-Werte (Euro-Einheit); keine Client-Berechnung.
    const lateEntryNotice =
        preview?.isPartialBooking &&
        typeof preview.reduction === "number" &&
        preview.reduction > 0 &&
        typeof preview.serviceGrossPrice === "number" &&
        typeof preview.originalGrossPrice === "number" ? (
            <div className={css.lateEntryNotice} data-testid='late-entry-notice'>
                <div className={css.lateEntryHint}>{t("lateEntry.courseFeeTotal", "Kursgebühr gesamt") as string}</div>
                <div className={css.lateEntryPrices}>
                    <span className={css.lateEntryServicePrice}>{renderAmount({ amount: preview.serviceGrossPrice, currency: currency.short as Currency })}</span>
                    <span className={css.lateEntryOriginalPrice}>{renderAmount({ amount: preview.originalGrossPrice, currency: currency.short as Currency })}</span>
                </div>
                <div className={css.lateEntryHint}>
                    {t("lateEntry.priceForXofY", {
                        defaultValue: "Preis für die verbleibenden {{eligible}} von {{total}} Kurseinheiten",
                        eligible: preview.eligibleLessonCount,
                        total: preview.totalLessonCount,
                    }) as string}
                </div>
            </div>
        ) : null

    return (
        <div className={css.root} id='booking-price-preview' data-testid='booking-price-preview'>
            {paymentTypeSwitch}
            {/* Beim Umschalten zeigt die Liste bis zur neuen Antwort die alte Preview: wie TotalPrice als veraltet
                markieren; bei einem Fehler nicht auf den Kursplan zurückfallen. */}
            {effectivePaymentType === PaymentType.Monthly && previewStatus !== "error" && (
                <Spin spinning={previewStatus === "loading"}>
                    <MonthlyPaymentList
                        installmentPlan={course.installmentRates}
                        discountInEuro={discountInEuro}
                        discountInPercentage={discountInPercentage}
                        attendeeId={attendeeId ?? undefined}
                        course={course}
                        preview={preview}
                    />
                </Spin>
            )}
            {lateEntryNotice}
            {additionalItemsSection}
            {discountCodeSection}
            {/* §4.5: Preview-Fehler ersetzt den Preisblock durch eine klare
                Fehlermeldung mit Retry — kein Fallback auf den Kurs-Listenpreis,
                der Zusatzposten und Späteinstiegslogik unterschlagen würde. */}
            {previewStatus === "error" && (
                <Alert
                    type='error'
                    showIcon
                    style={{ marginTop: margin.s }}
                    data-testid='price-preview-error'
                    message={t("pricePreview.errorTitle", "Der Preis konnte nicht berechnet werden.") as string}
                    description={t("pricePreview.errorHint", "Bitte erneut versuchen — eine Buchung ist erst nach erfolgreicher Preisberechnung möglich.") as string}
                    action={
                        <button type='button' onClick={() => setRetryNonce((nonce) => nonce + 1)} data-testid='price-preview-retry'>
                            {t("pricePreview.retry", "Erneut versuchen") as string}
                        </button>
                    }
                />
            )}
            {/* Web-Setting showTotalPriceInCheckout (default an): Kunden mit monatlicher
                Bezahlung können den Gesamtbetrag ausblenden. §4.5: während einer
                laufenden Neuberechnung ist der zuletzt bestätigte Preis sichtbar
                als veraltet markiert (Spin). */}
            {showTotalPrice !== false && previewStatus !== "error" && (
                <Spin spinning={previewStatus === "loading"}>
                    <TotalPrice paymentType={effectivePaymentType ?? undefined} preview={preview} baseGrossPrice={baseGrossPrice} creditToApply={creditToApply} remainingCredit={remainingCredit} />
                </Spin>
            )}
            {creditRedemption}
            {course.coursePrice[0].discount && attendeeId && (
                <MemberDiscount
                    course={course}
                    bookingData={booking}
                    attendeeId={attendeeId}
                    setDiscountInEuro={setDiscountInEuro}
                    setDiscountInPercentage={setDiscountInPercentage}
                    preview={preview}
                />
            )}
        </div>
    )
}

export default memo(BookingPricePreview)
